import { useEffect, useState } from "react";
import { useAuth } from "../components/AuthGate";
import { supabase } from "../lib/supabase";
import Logo from "../seller/components/Logo";
import { VENDOR_PORTAL_CSS } from "./styles";

const BETA_SECRET = process.env.REACT_APP_BETA_SECRET || "sixzero2026";

const fmtMoney = (cents) => (cents == null ? "—" : `$${Math.round(cents / 100).toLocaleString()}`);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

async function uploadImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const base64 = e.target.result.split(",")[1];
        const res = await fetch("/api/upload", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ data: base64, mime: file.type, purpose: "testimonial" }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Upload failed");
        resolve(data.url);
      } catch (err) { reject(err); }
    };
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.readAsDataURL(file);
  });
}

// Portal for an approved vendor to sign in (same magic-link AuthGate as
// buyer/seller/broker) and see their own inquiries + quotes/jobs, and manage
// testimonials — everything here reads/writes only rows RLS-scoped to their
// own vendor_profiles.owner_user_id, mirroring AccountApp's read pattern.
export default function VendorPortalApp() {
  const auth = useAuth();
  const uid = auth?.user?.id || null;
  const email = auth?.user?.email || null;

  const [claiming, setClaiming] = useState(true);
  const [claimError, setClaimError] = useState("");
  const [vendor, setVendor] = useState(null);
  const [tab, setTab] = useState("requests");
  const [inquiries, setInquiries] = useState(null);
  const [quotes, setQuotes] = useState(null);
  const [testimonials, setTestimonials] = useState(null);
  const [testiForm, setTestiForm] = useState({ client_name: "", quote: "", photo_url: "" });
  const [uploadingTesti, setUploadingTesti] = useState(false);
  const [savingTesti, setSavingTesti] = useState(false);

  useEffect(() => {
    if (!supabase || !uid || !email) return;
    let cancelled = false;
    (async () => {
      setClaiming(true);
      setClaimError("");
      const claimRes = await fetch("/api/vendors", {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-beta-token": BETA_SECRET },
        body: JSON.stringify({ action: "claim", email, user_id: uid }),
      }).then((r) => r.json()).catch(() => ({ error: "Connection error" }));
      if (cancelled) return;
      if (claimRes.error) { setClaimError(claimRes.error); setClaiming(false); return; }
      const { data } = await supabase.from("vendor_profiles").select("*").eq("owner_user_id", uid).maybeSingle();
      if (cancelled) return;
      setVendor(data || null);
      setClaiming(false);
    })();
    return () => { cancelled = true; };
  }, [uid, email]);

  useEffect(() => {
    if (!supabase || !vendor?.id) return;
    supabase.from("vendor_inquiries").select("*").eq("vendor_id", vendor.id)
      .order("created_at", { ascending: false }).then(({ data }) => setInquiries(data || []));
    supabase.from("vendor_quotes").select("*").eq("vendor_id", vendor.id)
      .order("created_at", { ascending: false }).then(({ data }) => setQuotes(data || []));
    supabase.from("vendor_testimonials").select("*").eq("vendor_id", vendor.id)
      .order("created_at", { ascending: false }).then(({ data }) => setTestimonials(data || []));
  }, [vendor?.id]);

  const markRead = async (id) => {
    if (!supabase) return;
    await supabase.from("vendor_inquiries").update({ status: "read" }).eq("id", id);
    setInquiries((prev) => prev.map((i) => (i.id === id ? { ...i, status: "read" } : i)));
  };

  const handleTestiPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingTesti(true);
    try {
      const url = await uploadImage(file);
      setTestiForm((s) => ({ ...s, photo_url: url }));
    } catch { /* silent — user can retry */ }
    setUploadingTesti(false);
  };

  const addTestimonial = async () => {
    if (!supabase || !vendor?.id || !testiForm.quote.trim()) return;
    setSavingTesti(true);
    const { data, error } = await supabase.from("vendor_testimonials").insert({
      vendor_id: vendor.id,
      client_name: testiForm.client_name.trim() || null,
      quote: testiForm.quote.trim().slice(0, 600),
      photo_url: testiForm.photo_url || null,
    }).select().single();
    setSavingTesti(false);
    if (!error && data) {
      setTestimonials((prev) => [data, ...(prev || [])]);
      setTestiForm({ client_name: "", quote: "", photo_url: "" });
    }
  };

  const removeTestimonial = async (id) => {
    if (!supabase) return;
    await supabase.from("vendor_testimonials").delete().eq("id", id);
    setTestimonials((prev) => prev.filter((t) => t.id !== id));
  };

  const home = () => (window.location.href = "/");
  const newCount = (inquiries || []).filter((i) => i.status === "new").length;

  if (!supabase) {
    return (
      <div className="vp-app">
        <style>{VENDOR_PORTAL_CSS}</style>
        <div className="vp-loading">Vendor portal requires sign-in to be configured.</div>
      </div>
    );
  }

  return (
    <div className="vp-app">
      <style>{VENDOR_PORTAL_CSS}</style>
      <header className="vp-top">
        <div className="vp-brand" onClick={home}>
          <Logo />
          <div className="vp-brand-name">6zero.ai</div>
        </div>
        <button type="button" className="vp-back" onClick={home}>home</button>
      </header>

      <main className="vp-main">
        {claiming ? (
          <div className="vp-loading">Loading your portal…</div>
        ) : claimError ? (
          <div className="vp-claim">
            <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, marginBottom: 10 }}>
              {claimError.includes("already linked") ? "Already linked" : "No application found"}
            </h2>
            <p style={{ color: "#5b7365", fontSize: 14, lineHeight: 1.7, marginBottom: 18 }}>
              {claimError.includes("already linked")
                ? "This vendor profile is already linked to a different account. If that's wrong, contact support."
                : `We couldn't find a vendor application under ${email}. Apply to the marketplace first, using the same email you sign in with.`}
            </p>
            {!claimError.includes("already linked") && (
              <button className="vp-btn" onClick={() => (window.location.href = "/join")}>Apply to the marketplace →</button>
            )}
          </div>
        ) : (
          <>
            <h1 className="vp-h1">{vendor.name}</h1>
            <div className="vp-sub">{vendor.type?.replace(/_/g, " ")}</div>
            <div className={`vp-status-pill ${vendor.status}`}>
              {vendor.status === "active" ? "● Live on 6zero.ai" : vendor.status === "draft" ? "○ Pending review" : "○ Suspended"}
            </div>

            <div className="vp-tabs">
              <button className={`vp-tab${tab === "requests" ? " active" : ""}`} onClick={() => setTab("requests")}>
                Requests{newCount > 0 && <span className="vp-badge">{newCount}</span>}
              </button>
              <button className={`vp-tab${tab === "quotes" ? " active" : ""}`} onClick={() => setTab("quotes")}>Quotes & jobs</button>
              <button className={`vp-tab${tab === "testimonials" ? " active" : ""}`} onClick={() => setTab("testimonials")}>Testimonials & photos</button>
            </div>

            {tab === "requests" && (
              inquiries === null ? <div className="vp-loading">Loading…</div>
              : inquiries.length === 0 ? <div className="vp-empty">No requests yet — they'll show up here as buyers and sellers reach out.</div>
              : inquiries.map((i) => (
                <div key={i.id} className="vp-card">
                  <div className="vp-card-head">
                    <div>
                      <div className="vp-card-title">{i.name}</div>
                      <div className="vp-card-meta">{i.email}{i.phone ? ` · ${i.phone}` : ""} · {fmtDate(i.created_at)}</div>
                      {i.property_address && <div className="vp-card-meta">{i.property_address}</div>}
                    </div>
                    <span className={`vp-status ${i.status}`}>{i.status}</span>
                  </div>
                  {i.message && <div className="vp-card-msg">"{i.message}"</div>}
                  {i.status === "new" && <button className="vp-btn" onClick={() => markRead(i.id)}>Mark as read</button>}
                </div>
              ))
            )}

            {tab === "quotes" && (
              quotes === null ? <div className="vp-loading">Loading…</div>
              : quotes.length === 0 ? <div className="vp-empty">No quotes generated yet — these appear when a seller requests you at a stage.</div>
              : quotes.map((q) => (
                <div key={q.id} className="vp-card">
                  <div className="vp-card-head">
                    <div>
                      <div className="vp-card-title">{q.stage_id?.replace(/_/g, " ") || "Quote"} — {fmtMoney(q.total_cents)}</div>
                      <div className="vp-card-meta">{fmtDate(q.created_at)}{q.scheduled_at ? ` · scheduled ${fmtDate(q.scheduled_at)}` : ""}</div>
                    </div>
                    <span className={`vp-status ${q.status}`}>{q.status}</span>
                  </div>
                  {q.scope && <div className="vp-card-msg">{q.scope}</div>}
                </div>
              ))
            )}

            {tab === "testimonials" && (
              <>
                <div className="vp-testi-form">
                  <input className="vp-input" placeholder="Client name (optional)" value={testiForm.client_name}
                    onChange={(e) => setTestiForm((s) => ({ ...s, client_name: e.target.value }))} />
                  <textarea className="vp-textarea" placeholder="What did they say about your work?" value={testiForm.quote}
                    onChange={(e) => setTestiForm((s) => ({ ...s, quote: e.target.value }))} maxLength={600} />
                  <label className="vp-btn" style={{ display: "inline-block", cursor: "pointer" }}>
                    {uploadingTesti ? "Uploading…" : testiForm.photo_url ? "Photo added ✓" : "+ Add a photo"}
                    <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleTestiPhoto} disabled={uploadingTesti} style={{ display: "none" }} />
                  </label>
                  <button className="vp-btn" style={{ marginLeft: 8 }} disabled={!testiForm.quote.trim() || savingTesti} onClick={addTestimonial}>
                    {savingTesti ? "Saving…" : "Add testimonial"}
                  </button>
                </div>
                {testimonials === null ? <div className="vp-loading">Loading…</div>
                  : testimonials.length === 0 ? <div className="vp-empty">No testimonials yet — add one above to show on your marketplace profile.</div>
                  : (
                    <div className="vp-testi-grid">
                      {testimonials.map((t) => (
                        <div key={t.id} className="vp-testi-card">
                          <button className="vp-testi-remove" onClick={() => removeTestimonial(t.id)}>×</button>
                          {t.photo_url && <img src={t.photo_url} alt="" className="vp-testi-photo" />}
                          <div style={{ fontSize: 13, fontStyle: "italic", color: "#3d4f45", marginBottom: 6 }}>"{t.quote}"</div>
                          {t.client_name && <div className="vp-card-meta">— {t.client_name}</div>}
                        </div>
                      ))}
                    </div>
                  )}
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}
